import type { ReactNode } from 'react';
import { LifecycleStatusTag } from 'components/lifecycle-status-tag';
import {
  ActionableNotification,
  IconButton,
  InlineLoading,
  Tag,
} from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { PlainLink } from 'components/link';
import {
  isShapeCarryingDomainKey,
  scenarioHref,
} from 'lib/evaluation-case/scenario-domains';
import type { CaseDomain, RunScenarioRow } from './types';
import styles from './case-management.module.scss';

interface CaseDomainRowsProps {
  rows: readonly RunScenarioRow[] | undefined;
  loading: boolean;
  error: Error | null;
  /** Opens the Scenario Picker for a shape-carrying domain, case-wide (areaId null). */
  onEdit: (domain: CaseDomain, currentScenarioId: number | null) => void;
  /** Opens the Price Deck picker for this run. */
  onEditPriceDeck: () => void;
  /** Shown right under the Type Curve row: what the case's Type Curve brings in, and its manager. */
  typeCurveDetails?: ReactNode;
  /** Asks run_scenarios again, after a failed check. */
  onRetry: () => void;
}

/**
 * (13.3) The scenario this run uses for each of the eight domains, one row each. What each row
 * says comes from `run_scenarios`:
 *   - the shape-carrying domains and the Price Deck can be changed for this run (Edit), and say so
 *     when they are;
 *   - Type Curve and DCA are the case's, shown, not picked (13.1). The Type Curve row carries what
 *     that Type Curve brings into the package, and Manage Type Curves, right under it.
 * A missing or archived scenario is named on its row.
 *
 * @param {CaseDomainRowsProps} props - The rows and the edit handlers.
 * @return {JSX.Element} The rows.
 */
export function CaseDomainRows({
  rows,
  loading,
  error,
  onEdit,
  onEditPriceDeck,
  typeCurveDetails,
  onRetry,
}: CaseDomainRowsProps) {
  const renderRow = (row: RunScenarioRow) => {
    const href = scenarioHref(row);
    const editLabel =
      row.domain === 'price_deck'
        ? 'Edit Price Deck for this run'
        : `Edit ${row.label} scenario for this run`;
    const { domain } = row;
    const onEditRow = isShapeCarryingDomainKey(domain)
      ? () => onEdit(domain, row.scenarioId)
      : domain === 'price_deck'
        ? onEditPriceDeck
        : null;
    return (
      <div
        className={styles.domainRow}
        data-testid={`case-domain-row-${row.domain}`}
      >
        <span className={styles.domainLabel}>{row.label}</span>
        <div className={styles.domainValue}>
          {row.scenarioName !== null && href !== null ? (
            <PlainLink className={styles.scenarioLink} href={href} openInNewTab>
              {row.scenarioName}
            </PlainLink>
          ) : (
            <span className={styles.domainProblem}>No scenario</span>
          )}
          <span className={styles.domainTags}>
            {row.overridden && (
              <Tag size="sm" type="blue">
                This run only
              </Tag>
            )}
            {row.archived && <LifecycleStatusTag status="archived" />}
            {!row.overridable && (
              <span className={styles.scenarioMeta}>From the case</span>
            )}
          </span>
        </div>
        {onEditRow && (
          <IconButton
            className={styles.domainEdit}
            label={editLabel}
            kind="ghost"
            size="sm"
            align="left"
            onClick={onEditRow}
          >
            <Edit />
          </IconButton>
        )}
      </div>
    );
  };

  // The Type Curve figures and manager read the case, not this check, so they show while the
  // rows load or fail too. They live in the one keyed group, with the row's slot empty until the
  // row arrives, so the switch from loading to rows MOVES the group rather than remounting it:
  // an open Manage Type Curves stays open, with its unapplied picks.
  const typeCurveGroup = (row?: RunScenarioRow) => (
    <div key="type_curve" className={styles.domainGroup}>
      {row ? renderRow(row) : null}
      {typeCurveDetails ? (
        <div className={styles.domainDetails} data-testid="case-type-curve">
          {typeCurveDetails}
        </div>
      ) : null}
    </div>
  );

  let status: ReactNode = null;
  if (error) {
    status = (
      <ActionableNotification
        key="status"
        className={styles.notification}
        inline
        kind="error"
        // hasFocus off: its auto-focus scrolls the panel (page jump).
        hasFocus={false}
        lowContrast
        hideCloseButton
        title="Couldn't load this run's scenarios"
        subtitle={error.message}
        actionButtonLabel="Retry"
        onActionButtonClick={onRetry}
      />
    );
  } else if (!rows && loading) {
    status = (
      <InlineLoading
        key="status"
        description="Loading this run's scenarios..."
      />
    );
  }

  const showRows = !error && rows !== undefined;
  return (
    <div
      className={styles.domainList}
      // Only once there are rows to name: "no rows yet" reads as the list being absent.
      data-testid={showRows ? 'case-domain-rows' : undefined}
    >
      {showRows
        ? rows.map((row) => {
            if (row.domain === 'type_curve') return typeCurveGroup(row);
            return (
              <div key={row.domain} className={styles.domainGroup}>
                {renderRow(row)}
              </div>
            );
          })
        : [status, typeCurveDetails ? typeCurveGroup() : null]}
    </div>
  );
}
